import React from "react";
import { Op } from "quill-delta";

import {
  ChoiceOptionResponse,
  DataBlockReference,
  DataBlockReferencesResponse,
  DataBlockResponse,
  DataFieldBaseResponse,
  DataTemplateResponse,
  DynamicValueResponse,
  FieldChangeResponse,
} from "@/redux/datablocks/types";
import { DataBlockRequestEditModalsState } from "@/features/evidence-requests/list/types";
import { SimpleModalProps } from "@/components_v2/Modal/types";
import { CARD_VARIANTS } from "@/features/datablocks/details/constants";
import { BaseUserResponse } from "@/redux/users/types";
import { DataFileResponse } from "@/redux/files/types";
import { SubjectResponse } from "@/redux/organizations/types";
import { ChapterResponse } from "@/redux/chapters/types";
import { CountryResponse, LanguageResponse } from "@/redux/common/types";

export interface DataBlockContainerProps {
  initialDataBlock?: DataBlockResponse;
  showCtas?: boolean;
}

export interface DataBlockSidebarProps {
  dataBlock: DataBlockResponse;
  dataTemplate: DataTemplateResponse;
  showCtas?: boolean;
}

export interface DataBlockActionButtonsProps {
  dataBlock: DataBlockResponse;
  dataTemplate: DataTemplateResponse;
  setOpenDeleteModal: (value: boolean) => void;
  setEditRequestModal: (value: DataBlockRequestEditModalsState) => void;
}

export interface AddContributorModalProps extends SimpleModalProps {
  dataBlock: DataBlockResponse;
  currentContributorIds: number[];
}

export interface AddContributorsFormValues {
  contributors: number[];
}

export interface DataBlockReferenceType {
  title: string;
  icon: React.ComponentType<{ size?: number }>;
  getLink: (value: DataBlockReference) => string;
}

export type DataBlockReferencesTypes = {
  surveys: DataBlockReferenceType;
  surveyViews: DataBlockReferenceType;
  discussions: DataBlockReferenceType;
  comments: DataBlockReferenceType;
  chapters: DataBlockReferenceType;
};

export interface DataBlockReferencesProps {
  references: DataBlockReferencesResponse;
}

export interface DataBlockVersionsProps {
  dataBlockId: number;
}

export type CardVariant = (typeof CARD_VARIANTS)[keyof typeof CARD_VARIANTS];

export interface ContributorsProps {
  dataBlock: DataBlockResponse;
  canEdit: boolean;
  showCtas?: boolean;
}

export enum DiffVariant {
  OLD = "old",
  NEW = "new",
}

export type CustomChoiceOption = Omit<
  ChoiceOptionResponse,
  "disabled" | "category" | "relatedOptions" | "order"
>;

export type RelatedDataItem =
  | ChoiceOptionResponse
  | CustomChoiceOption
  | BaseUserResponse
  | DataFileResponse
  | SubjectResponse
  | ChapterResponse
  | LanguageResponse
  | CountryResponse;

export interface ScaleRelation {
  objectId: number;
  scale?: number | null;
}

export interface ResolvedItem {
  id: number;
  item: RelatedDataItem | null;
  scale?: RelatedDataItem | null;
}

export interface UseHistoryRelatedDataReturn {
  lookup: Map<string, RelatedDataItem>;
  isLoading: boolean;
}
export interface HistoryProps {
  dataBlockId: number;
}

export interface ChangeRowProps {
  change: FieldChangeResponse;
  lookup: Map<string, RelatedDataItem>;
}

export interface ValueDiffProps<
  T extends string | ResolvedItem = string | ResolvedItem,
> {
  oldItems: T[] | null;
  newItems: T[] | null;
  Component: React.ComponentType<{ value: T; className?: string }>;
  showArrow?: boolean;
}

export interface FieldValueDiffDisplayProps {
  field: DataFieldBaseResponse;
  oldValue: DynamicValueResponse;
  newValue: DynamicValueResponse;
  lookup: Map<string, RelatedDataItem>;
}

export interface ExtractChangeValueParams {
  field: DataFieldBaseResponse;
  relations: number[] | ScaleRelation[] | null;
  jsonValue: Record<string, string | string[] | null> | null;
}

export interface SelectComponentProps {
  value: ResolvedItem;
  className?: string;
}

export interface TextDisplayProps {
  value: string;
  className?: string;
}

export interface TextDiffModalProps extends SimpleModalProps {
  oldValue: string;
  newValue: string;
}

export interface TextSidebarDiffProps {
  oldValue: string;
  newValue: string;
}

export interface TaggedOp {
  op: Op;
  dirty: boolean;
}

export interface Segment {
  ops: Op[];
  dirty: boolean;
}

export enum DeltaOperation {
  EQUAL = "equal",
  DELETE = "delete",
  INSERT = "insert",
}

export interface DiffPart {
  op: DeltaOperation;
  text: string;
  attributes?: Record<string, any>;
}
